/**
 * SG PreSet — Confirmation of Registrable Controllers (B32108)
 *
 * Template: company_name, company_registration_no, confirmation_date,
 *           controllers_list ([[if controllers_list]] …), Signers
 */
import {
  ensureB15JurisdictionNameMap,
  resolveB15CodeToName,
} from '../../notice-corporate/composables/b15JurisdictionDisplay';
import type { EntityControllerManageRow } from './entityControllersApi';

export const SG_CONFIRMATION_CONTROLLERS_TASK_CODE = 'B32108';

export interface ConfirmationControllerSelection extends EntityControllerManageRow {
  selected: boolean;
  /** Vue row key for manually added rows (not from L_CONTROLLERMANAGE refresh) */
  _localKey?: string;
}

let manualControllerKeySeq = 0;

export function nextManualControllerLocalKey(): string {
  manualControllerKeySeq += 1;
  return `manual-${manualControllerKeySeq}-${Date.now()}`;
}

export function createEmptyControllerSelection(selected = true): ConfirmationControllerSelection {
  return {
    ctrlIdx: '',
    referencesContactCd: '',
    fullName: '',
    categoryCd: '',
    legalForm: '',
    jurisdictionCd: '',
    incorporationStatute: '',
    corporateEntityRegister: '',
    contactRegNo: '',
    regNo: '',
    nonRegNo: '',
    becomingDate: '',
    cessationDate: '',
    selected,
    _localKey: nextManualControllerLocalKey(),
  };
}

/** Chat officer-list / contact pick → controller row */
export function mapOfficerToControllerSelection(
  person: Record<string, unknown>,
  selected = true,
): ConfirmationControllerSelection {
  const fullName = trim(
    person.name ?? person.contactName ?? person.FULL_NAME ?? person.fullName,
  );
  return {
    ctrlIdx: trim(person.ctrlIdx ?? person.CTRL_IDX ?? person.id ?? person.officerIdx),
    referencesContactCd: trim(person.contactCd ?? person.CONTACT_CD ?? person.referencesContactCd),
    fullName,
    categoryCd: trim(person.categoryCd ?? person.CATEGORY_CD),
    legalForm: trim(person.legalForm ?? person.LEGAL_FORM),
    jurisdictionCd: trim(person.jurisdiction ?? person.jurisdictionCd ?? person.JURISDICTION),
    incorporationStatute: trim(person.incorporationStatute ?? person.INCORPORATION_STATUTE),
    corporateEntityRegister: trim(
      person.corporateEntityRegister ?? person.CORPORATE_ENTITY_REGISTER,
    ),
    contactRegNo: trim(
      person.contactRegNo ?? person.CONTACT_REG_NO ?? person.contactIdUen ?? person.CONTACT_ID_UEN,
    ),
    regNo: trim(person.regNo ?? person.REG_NO),
    nonRegNo: trim(person.nonRegNo ?? person.NON_REG_NO),
    becomingDate: trim(
      person.becomingDate ?? person.BECOMING_DATE ?? person.appointmentDate ?? person.dateOfAppointed,
    ),
    cessationDate: '',
    selected,
    _localKey: nextManualControllerLocalKey(),
  };
}

export function isManualControllerRow(row: ConfirmationControllerSelection): boolean {
  return !!trim(row._localKey);
}

export interface ConfirmationControllersFormModel {
  companyName: string;
  companyRegistrationNo: string;
  confirmationDate: string;
  /** L_CONTROLLERMANAGE rows — user toggles inclusion in declaration */
  controllers: ConfirmationControllerSelection[];
}

export function createEmptyConfirmationControllersForm(): ConfirmationControllersFormModel {
  return {
    companyName: '',
    companyRegistrationNo: '',
    confirmationDate: '',
    controllers: [],
  };
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function categoryLabel(cd: string): string {
  const c = trim(cd).toUpperCase();
  if (c === 'B09001') return 'Corporate';
  if (c === 'B09002') return 'Individual';
  return c;
}

export function selectionsFromEntityRows(
  rows: EntityControllerManageRow[],
  preselected = true,
): ConfirmationControllerSelection[] {
  return rows.map((r) => ({ ...r, selected: preselected }));
}

export function applyEntityBasicsToConfirmationForm(
  model: ConfirmationControllersFormModel,
  ent: Record<string, unknown> | null | undefined,
): void {
  if (!ent) return;
  const name = trim(ent.companyName ?? ent.entityName ?? ent.ENTITY_NAME);
  if (name) model.companyName = name;
  const reg = trim(
    ent.registrationNo ?? ent.regNo ?? ent.REG_NO ?? ent.companyRegistrationNo,
  );
  if (reg) model.companyRegistrationNo = reg;
}

function buildControllerListItems(
  model: ConfirmationControllersFormModel,
  b15Map: Map<string, string>,
) {
  return model.controllers
    .filter((c) => c.selected)
    .map((c) => {
      const jurisdictionName = resolveB15CodeToName(c.jurisdictionCd, b15Map) || c.jurisdictionCd;
      return {
        controller_name: c.fullName,
        full_name: c.fullName,
        category: categoryLabel(c.categoryCd),
        category_cd: c.categoryCd,
        legal_form: c.legalForm,
        jurisdiction: jurisdictionName,
        jurisdiction_cd: c.jurisdictionCd,
        incorporation_statute: c.incorporationStatute,
        corporate_entity_register: c.corporateEntityRegister,
        contact_reg_no: c.contactRegNo || c.regNo || c.nonRegNo,
        reg_no: c.regNo,
        non_reg_no: c.nonRegNo,
        becoming_date: c.becomingDate,
        references_contact_cd: c.referencesContactCd,
        ctrl_idx: c.ctrlIdx,
      };
    });
}

export async function toConfirmationControllersTemplateVariables(
  model: ConfirmationControllersFormModel,
): Promise<Record<string, unknown>> {
  const b15Map = await ensureB15JurisdictionNameMap();
  const list = buildControllerListItems(model, b15Map);
  const listJson = list.length > 0 ? JSON.stringify(list) : '';

  return {
    company_name: trim(model.companyName),
    company_registration_no: trim(model.companyRegistrationNo),
    confirmation_date: trim(model.confirmationDate),
    controllers_list: list,
    controllers: list,
    controller: list,
    controllers_list_json: listJson,
    signers: '',
    Signers: '',
  };
}

export function isConfirmationControllersFormComplete(
  model: ConfirmationControllersFormModel,
): boolean {
  if (!trim(model.companyName) || !trim(model.companyRegistrationNo)) return false;
  if (!trim(model.confirmationDate)) return false;
  return model.controllers.some((c) => c.selected && trim(c.fullName));
}

export async function buildConfirmationControllersTaskFormData(
  model: ConfirmationControllersFormModel,
  entityCd: string,
  signersJson: string,
): Promise<Record<string, unknown>> {
  const vars = await toConfirmationControllersTemplateVariables(model);
  const ec = trim(entityCd);
  return {
    ...vars,
    formType: SG_CONFIRMATION_CONTROLLERS_TASK_CODE,
    entityCd: ec,
    entity: ec,
    entityName: { value: trim(model.companyName) },
    companyName: trim(model.companyName),
    companyRegistrationNo: trim(model.companyRegistrationNo),
    signers: signersJson,
    Signers: signersJson,
  };
}
